"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.UnavPeriodSection = void 0;
const ui_1 = require("@app/component/ui");
const dropdown_input_1 = require("@app/component/ui/forms/dropdown-input/dropdown-input");
const form_grid_layout_1 = require("@app/component/ui/forms/form-grid-layout");
const time_input_1 = require("@app/component/ui/input/time-input/time-input");
const graphql_1 = require("@app/gql-types/graphql");
const store_1 = require("@app/store");
const util_1 = require("@app/util");
const maybe_unwrap_1 = require("@app/util/apollo/maybe-unwrap");
const maybe_1 = require("@app/util/functional/maybe");
const use_workspace_unavailability_categories_1 = require("@app/util/hooks/use-workspace-unavailability-categories");
const useWorkspace_1 = require("@app/util/hooks/useWorkspace");
const moment_1 = __importDefault(require("moment"));
const react_1 = __importDefault(require("react"));
const react_hook_form_1 = require("react-hook-form");
const unavailability_context_1 = require("../unavailability-context");
const period_type_booleans_1 = require("../util/period-type-booleans");
const use_unav_date_controls_1 = require("../util/use-unav-date-controls");
const modal_section_1 = require("./modal-section");
const UnavPeriodSection = () => {
    const { formControl, setValue, timePeriodInput, setTimePeriodInput, isEmployeeCalendar, canEdit, isEditing, initialData, toggleSection, } = (0, unavailability_context_1.useUnavailability)();
    const workspace = (0, useWorkspace_1.useWorkspace)();
    const shifts = (0, store_1.useRootSelector)((state) => isEmployeeCalendar ? (state.me.shifts ?? []) : state.shifts);
    const categories = (0, use_workspace_unavailability_categories_1.getWorkspaceUnavailabilityCategories)(workspace);
    const categoryId = (0, react_hook_form_1.useWatch)({ control: formControl, name: 'categoryId' });
    const currentCategory = categories.find((cat) => cat.id === categoryId);
    const periodTypeController = (0, react_hook_form_1.useController)({
        control: formControl,
        name: 'periodType',
    });
    const periodType = (0, react_hook_form_1.useWatch)({
        control: formControl,
        name: 'periodType',
    });
    const { periodStartController, periodEndController, handleTimeChange } = (0, use_unav_date_controls_1.useUnavDateControls)({
        formControl,
        setValue,
        timePeriodInput,
        setTimePeriodInput,
    });
    const userId = (0, react_hook_form_1.useWatch)({ control: formControl, name: 'user' });
    const shiftsInDay = shifts.filter((shift) => shift.userId === userId &&
        (0, moment_1.default)(periodStartController.field.value).isSame(shift?.period?.start, 'day'));
    const isAvailabilityOrUnavailability = categoryId === use_workspace_unavailability_categories_1.AVAILABILITY_VALUE || categoryId === use_workspace_unavailability_categories_1.UNAVAILABILITY_VALUE;
    const periodStartMoment = new maybe_1.Maybe(timePeriodInput.start)
        .map((_start) => (0, moment_1.default)(_start))
        .get(() => undefined);
    const periodEndMoment = new maybe_1.Maybe(timePeriodInput.end)
        .map((_end) => (0, moment_1.default)(_end))
        .get(() => undefined);
    const periodStartValue = new maybe_1.Maybe(periodStartMoment)
        .map((_start) => _start.get('hours') * 60 + _start.get('minutes'))
        .get(() => 0);
    const periodEndValue = new maybe_1.Maybe(periodEndMoment)
        .map((_end) => _end.get('hours') * 60 + _end.get('minutes'))
        .get(() => 0);
    // Slovensko + Dovolená -> options = Half day
    // Slovensko + non-dovolená -> options = Half day, Part day
    // Non slovensko + dovolená -> options = Half day, Part day
    // Non slovensko + non dovolená -> optios = Half day, Part day
    // s tím že pro HALF DAY bude pořád platit že musí existovat PRAVE JEDNA SMENA a NESMI BYT PREFERENCE
    const partOfDayOptions = [
        shiftsInDay.length === 1 && !isAvailabilityOrUnavailability
            ? {
                value: graphql_1.PeriodType.HalfDay,
                label: (0, util_1.t)('UNAV_HALF_DAY'),
            }
            : undefined,
        workspace?.country === 'SK' && currentCategory?.vacation === true
            ? undefined
            : {
                value: graphql_1.PeriodType.PartDay,
                label: (0, util_1.t)('MA_PART_OF_DAY'),
            },
    ].filter(util_1.isDefined);
    const halfDayAvailable = (0, period_type_booleans_1.canUseHalfDayOption)(shiftsInDay.length, isAvailabilityOrUnavailability);
    const partDayAvailable = (0, period_type_booleans_1.canUsePartDayOption)(workspace?.country ?? '', currentCategory?.vacation ?? false);
    react_1.default.useEffect(() => {
        const currentPeriodType = (0, period_type_booleans_1.decidePeriodType)((0, maybe_unwrap_1.unwrapMaybe)(periodTypeController.field.value), halfDayAvailable, partDayAvailable);
        if (periodTypeController.field.value !== currentPeriodType) {
            periodTypeController.field.onChange(currentPeriodType);
        }
        if (!halfDayAvailable &&
            !partDayAvailable &&
            periodTypeController.field.value !== currentPeriodType) {
            toggleSection('whole-day', true);
        }
    }, [categoryId, halfDayAvailable, partDayAvailable, periodTypeController]);
    const halfDayOptions = [
        {
            label: (0, util_1.t)('UNAV_FROM_START'),
            value: 'start',
        },
        {
            label: (0, util_1.t)('UNAV_TILL_END'),
            value: 'end',
        },
    ];
    const isHalfDay = periodType === graphql_1.PeriodType.HalfDay;
    const isPartDay = periodType === graphql_1.PeriodType.PartDay;
    const canManagerCalendarEdit = canEdit;
    const canEditEmployee = initialData?.categoryId === use_workspace_unavailability_categories_1.AVAILABILITY_VALUE ||
        initialData?.categoryId === use_workspace_unavailability_categories_1.UNAVAILABILITY_VALUE;
    const employeeEditableFields = isEmployeeCalendar
        ? canEditEmployee
            ? true
            : !isEditing
        : canManagerCalendarEdit;
    return (react_1.default.createElement(modal_section_1.ModalSection, { id: 'period', init: () => {
            const defaultPeriodType = shiftsInDay.length !== 1 || isAvailabilityOrUnavailability
                ? graphql_1.PeriodType.PartDay
                : graphql_1.PeriodType.HalfDay;
            const currentPeriodType = (0, period_type_booleans_1.decidePeriodType)((0, maybe_unwrap_1.unwrapMaybe)(defaultPeriodType), halfDayAvailable, partDayAvailable);
            periodTypeController.field.onChange(currentPeriodType);
            handleTimeChange({
                start: {
                    hours: 8,
                    minutes: 0,
                },
                end: {
                    hours: 16,
                    minutes: 30,
                },
            });
        }, cleanup: () => {
            periodTypeController.field.onChange(graphql_1.PeriodType.WholeDay);
            periodStartController.field.onChange((0, moment_1.default)(periodStartController.field.value).startOf('day'));
            periodEndController.field.onChange((0, moment_1.default)(periodEndController.field.value).endOf('day'));
        } },
        react_1.default.createElement(form_grid_layout_1.FormGridLayout, null,
            react_1.default.createElement(ui_1.RadioGroup, { onChange: periodTypeController.field.onChange, value: (0, maybe_unwrap_1.unwrapMaybe)(periodTypeController.field.value), options: partOfDayOptions, disabled: !employeeEditableFields }),
            react_1.default.createElement("div", null),
            isPartDay && partDayAvailable && (react_1.default.createElement(react_1.default.Fragment, null,
                react_1.default.createElement(time_input_1.TimeInput, { label: (0, util_1.t)('START'), onChange: (v) => {
                        const hours = v / 60;
                        const minutes = v % 60;
                        handleTimeChange({
                            start: {
                                hours,
                                minutes,
                            },
                            end: undefined,
                        });
                    }, value: periodStartValue, disabled: !employeeEditableFields }),
                react_1.default.createElement(time_input_1.TimeInput, { label: (0, util_1.t)('END'), onChange: (v) => {
                        const minutes = v % 60;
                        const hours = (v - minutes) / 60;
                        handleTimeChange({
                            start: undefined,
                            end: {
                                hours,
                                minutes,
                            },
                        });
                    }, value: periodEndValue, disabled: !employeeEditableFields }))),
            isHalfDay && halfDayAvailable && (react_1.default.createElement(react_1.default.Fragment, null,
                react_1.default.createElement(dropdown_input_1.DropdownInput, { options: halfDayOptions, control: formControl, name: 'halfDayOption', sortedOptions: false, rules: {
                        required: {
                            value: isHalfDay,
                            message: (0, util_1.t)('FORM_ERR_MISSING_REQUIRED'),
                        },
                    }, disabled: !employeeEditableFields }))))));
};
exports.UnavPeriodSection = UnavPeriodSection;
